export default function KeywordSeoSection() {
  return (
    <section
      id="keywords-seo"
      className="py-20 sm:py-28 bg-[#0d0d0d] relative"
      aria-label="Senior fitness program information for men over 60"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#c8a96e]/15 to-transparent" />

      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-px bg-[#c8a96e]" />
          <span className="text-[#c8a96e] text-xs font-black tracking-[0.3em] uppercase">Why This Works</span>
        </div>

        <div className="mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-5">
            The Best <span className="text-[#c8a96e]">Strength Training Program</span> for Men Over 60
          </h2>
          <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-6">
            Finding the right <strong className="text-gray-300">workout plan for men over 60</strong> can be overwhelming. Most programs ignore the reality of training after 60 — slower recovery, joint issues, and a body that doesn't respond like it used to.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="bg-white/3 border border-white/8 rounded-sm p-6">
            <div className="flex items-start gap-4 mb-4">
              <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#c8a96e]/10 border border-[#c8a96e]/20 rounded-sm">
                <i className="ri-shield-check-line text-[#c8a96e] text-lg" />
              </span>
              <div>
                <h3 className="text-white text-lg font-black mb-2">Joint-Friendly Workouts for Seniors</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  This <strong className="text-gray-400">senior fitness program for men</strong> prioritizes joint safety without sacrificing results. Every exercise includes modifications for bad knees, shoulders, and back — so you can train hard without the pain.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/3 border border-white/8 rounded-sm p-6">
            <div className="flex items-start gap-4 mb-4">
              <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#c8a96e]/10 border border-[#c8a96e]/20 rounded-sm">
                <i className="ri-line-chart-line text-[#c8a96e] text-lg" />
              </span>
              <div>
                <h3 className="text-white text-lg font-black mb-2">Proven Muscle Building Over 60</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  <strong className="text-gray-400">Muscle building over 60</strong> is absolutely possible with the right approach. This program uses progressive overload designed specifically for older adults — building real strength without overtraining or injury.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/3 border border-white/8 rounded-sm p-6">
            <div className="flex items-start gap-4 mb-4">
              <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#c8a96e]/10 border border-[#c8a96e]/20 rounded-sm">
                <i className="ri-scales-3-line text-[#c8a96e] text-lg" />
              </span>
              <div>
                <h3 className="text-white text-lg font-black mb-2">Lose Weight After 60 Male</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Trying to <strong className="text-gray-400">lose weight after 60 male</strong>? This isn't a crash diet. It's a sustainable system combining <strong className="text-gray-400">strength training over 60</strong> with simple nutrition guidelines that actually work for men in their 60s and beyond.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/3 border border-white/8 rounded-sm p-6">
            <div className="flex items-start gap-4 mb-4">
              <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#c8a96e]/10 border border-[#c8a96e]/20 rounded-sm">
                <i className="ri-trophy-line text-[#c8a96e] text-lg" />
              </span>
              <div>
                <h3 className="text-white text-lg font-black mb-2">Fitness Transformation Over 60</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  A real <strong className="text-gray-400">fitness transformation over 60</strong> takes time, consistency, and the right plan. Mason's 12-week blueprint is the <strong className="text-gray-400">best exercise program for 60 year old man</strong> who wants lasting results — not quick fixes.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#c8a96e]/5 border border-[#c8a96e]/20 rounded-sm p-8">
          <h3 className="text-white text-2xl font-black mb-4 flex items-center gap-3">
            <span className="w-8 h-8 flex items-center justify-center bg-[#c8a96e] rounded-sm flex-shrink-0">
              <i className="ri-lightbulb-line text-black text-base" />
            </span>
            What Makes This the Best Workout Plan for Men Over 60?
          </h3>
          <p className="text-gray-400 text-base leading-relaxed mb-5">
            Most <strong className="text-gray-300">workout plans for men over 60</strong> are just watered-down versions of programs built for younger lifters. This blueprint is different — it's designed from the ground up for how your body actually works at this age.
          </p>
          <ul className="space-y-3">
            {[
              'Progressive strength training that respects recovery time',
              'Joint-safe movement patterns with alternatives for every exercise',
              'Realistic nutrition guidelines for sustainable fat loss and muscle gain',
              'Built-in mobility and recovery protocols to prevent injury',
              'Proven by real men over 60 — including Mason himself at age 62',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-[#c8a96e] text-base" />
                </span>
                <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto">
            Whether you're looking for a <strong className="text-gray-400">senior fitness program for men</strong>, want to start <strong className="text-gray-400">muscle building over 60</strong>, or need <strong className="text-gray-400">joint-friendly workouts for seniors</strong> — this is the complete system you've been searching for.
          </p>
        </div>
      </div>
    </section>
  );
}